import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Package, X, Camera, DownloadSimple, UploadSimple, Tag } from '@phosphor-icons/react';
import { MaterialFormModal } from '../components/materials/MaterialFormModal';
import { CategoryManagerModal } from '../components/categories/CategoryManagerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PhotoCell = ({ item, onUpload, onDelete, size = 48 }) => {
  const inputRef = useRef(null);
  const hasPhoto = !!item.photo_url;
  return (
    <div
      onClick={() => inputRef.current?.click()}
      data-testid={`photo-cell-${item.id || item.material_id}`}
      style={{
        width: `${size}px`, height: `${size}px`, borderRadius: '8px',
        background: hasPhoto ? '#F4F5F7' : 'linear-gradient(135deg, #EEF1FC, #E2E8F7)',
        border: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', cursor: 'pointer', position: 'relative', flexShrink: 0
      }}
      title={hasPhoto ? 'Cliquer pour supprimer/changer' : 'Ajouter une photo'}
    >
      {hasPhoto ? (
        <>
          <img src={`${API_URL}${item.photo_url}?v=${item.id || item.material_id}`} alt={item.name}
               style={{ width: '100%', height: '100%', objectFit: 'cover' }}
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`photo-del-${item.id || item.material_id}`}
              style={{ position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              title="Supprimer la photo"
            ><X size={12} /></button>
          )}
        </>
      ) : (
        <Camera size={18} color="#9A9AA0" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }}
        style={{ display: 'none' }}
        data-testid={`photo-input-${item.id || item.material_id}`}
      />
    </div>
  );
};

const MaterialsCatalogPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/materials/catalog`, { withCredentials: true });
      setItems(res.data);
    } catch (e) {
      toast.error('Erreur chargement catalogue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return i.name.toLowerCase().includes(q) || (i.category || '').toLowerCase().includes(q);
  });

  const handleEdit = (m) => {
    setEditId(m.id);
    setShowModal(true);
  };

  const handlePhotoUpload = async (materialId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo trop lourde (max 10 Mo)'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/materials/catalog/${materialId}/photo`, fd, { withCredentials: true });
      toast.success('Photo ajoutee');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload photo');
    }
  };

  const handlePhotoDelete = async (materialId) => {
    if (!window.confirm('Supprimer la photo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/materials/catalog/${materialId}/photo`, { withCredentials: true });
      toast.success('Photo supprimee');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const [aiBusyId, setAiBusyId] = useState(null);

  const handleAiGenerate = async (materialId) => {
    setAiBusyId(materialId);
    try {
      await axios.post(`${API_URL}/api/materials/catalog/${materialId}/photo/generate`, {}, { withCredentials: true });
      toast.success('Photo IA generee');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation IA');
    } finally { setAiBusyId(null); }
  };

  const handlePhotoFromUrl = async (materialId) => {
    const url = window.prompt('Collez l\'URL d\'une image (clic droit sur une image Google -> Copier l\'adresse de l\'image) :');
    if (!url) return;
    try {
      const fd = new FormData();
      fd.append('url', url);
      await axios.post(`${API_URL}/api/materials/catalog/${materialId}/photo/from-url`, fd, { withCredentials: true });
      toast.success('Photo importee');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer le materiau "${m.name}" ? Les listes de chantiers le contenant seront mises a jour.`)) return;
    try {
      await axios.delete(`${API_URL}/api/materials/catalog/${m.id}`, { withCredentials: true });
      toast.success('Supprime');
      load();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  // ----- Multi-selection helpers ------------------------------------------
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const visibleIds = filtered.map(m => m.id);
      const allSelected = visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} materiau(x) ? Les listes de chantiers seront mises a jour.`)) return;
    let ok = 0, ko = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        await axios.delete(`${API_URL}/api/materials/catalog/${id}`, { withCredentials: true });
        ok++;
      } catch { ko++; }
    }
    toast.success(`${ok} supprime(s)${ko ? `, ${ko} erreur(s)` : ''}`);
    clearSelection();
    load();
  };

  const handleBulkCategory = async () => {
    if (selectedIds.size === 0 || !bulkCategoryValue.trim()) return;
    let ok = 0, ko = 0;
    for (const id of Array.from(selectedIds)) {
      const m = items.find(x => x.id === id);
      if (!m) { ko++; continue; }
      try {
        await axios.put(`${API_URL}/api/materials/catalog/${id}`, {
          name: m.name,
          category: bulkCategoryValue.trim(),
          unit: m.unit,
          price_ht: m.price_ht,
          tva_rate: m.tva_rate,
          min_threshold: m.min_threshold || 0,
          coefficient_m2: m.coefficient_m2 ?? 1,
        }, { withCredentials: true });
        ok++;
      } catch { ko++; }
    }
    toast.success(`${ok} categorise(s)${ko ? `, ${ko} erreur(s)` : ''}`);
    setBulkCategoryOpen(false);
    setBulkCategoryValue('');
    clearSelection();
    load();
  };

  const handleBulkExport = async () => {
    if (selectedIds.size === 0) return;
    // Use the existing library export endpoint with an ids filter we add inline
    try {
      const params = new URLSearchParams({ kind: 'materiaux', ids: Array.from(selectedIds).join(',') });
      const r = await axios.get(`${API_URL}/api/invoicing/library/export?${params.toString()}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `materiaux_selection_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur export selection');
    }
  };

  // Unique categories list for the bulk-category dropdown
  const uniqueCategories = Array.from(new Set(items.map(m => m.category).filter(Boolean))).sort();

  return (
    <div data-testid="materials-catalog-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}><Package size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Catalogue matériaux</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#5E5F66' }}>Liste de référence appliquée à tous les chantiers. Les prix ne sont pas visibles par les salariés.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="export-materials-btn"
            onClick={async () => {
              try {
                const r = await axios.get(`${API_URL}/api/invoicing/library/export?kind=materiaux`, { withCredentials: true, responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = `bibliotheque_materiaux_${new Date().toISOString().slice(0,10)}.xlsx`; a.click(); URL.revokeObjectURL(url);
              } catch { toast.error('Erreur export'); }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #047857', background: 'white', color: '#047857', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
          >
            <DownloadSimple size={14} weight="bold" /> Export Excel
          </button>
          <label
            data-testid="import-materials-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #002FA7', background: 'white', color: '#002FA7', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
          >
            <UploadSimple size={14} weight="bold" /> Importer
            <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append('file', f);
              try {
                const r = await axios.post(`${API_URL}/api/imports/materiaux`, fd, { withCredentials: true });
                const { created = 0, updated = 0, errors = [] } = r.data || {};
                toast.success(`${created} créé(s), ${updated} mis à jour${errors.length ? `, ${errors.length} erreur(s)` : ''}`);
                load();
              } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur import'); }
              e.target.value = '';
            }} />
          </label>
          <button
            type="button"
            data-testid="download-materials-template-btn"
            onClick={async () => {
              try {
                const r = await axios.get(`${API_URL}/api/imports/template?kind=materiaux`, { withCredentials: true, responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'modele_materiaux.xlsx'; a.click(); URL.revokeObjectURL(url);
              } catch { toast.error('Erreur modèle'); }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px dashed #6B7280', background: 'transparent', color: '#6B7280', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}
          >
            Modèle
          </button>
          <button
            type="button"
            data-testid="manage-material-categories-btn"
            onClick={() => setShowCategoryManager(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #7C3AED', background: 'white', color: '#7C3AED', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title="Ajouter, renommer ou supprimer des catégories de matériaux"
          >
            <Tag size={14} weight="bold" /> Gérer les catégories
          </button>
          <button className="btn btn-primary" data-testid="add-material-btn" onClick={() => { setEditId(null); setShowModal(true); }}>
            <Plus size={16} weight="bold" /> Ajouter un matériau
          </button>
        </div>
      </div>

      <input
        type="search"
        placeholder="Rechercher..."
        className="form-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: '12px', maxWidth: '340px' }}
      />

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every(m => selectedIds.has(m.id))}
                    onChange={toggleAllVisible}
                    data-testid="mat-select-all"
                    aria-label="Tout selectionner"
                  />
                </th>
                <th style={{ width: '60px' }}>Photo</th>
                <th>Nom</th>
                <th>Categorie</th>
                <th>Unite</th>
                <th style={{ textAlign: 'right' }}>Prix HT</th>
                <th style={{ textAlign: 'right' }}>TVA</th>
                <th style={{ textAlign: 'right' }}>Prix TTC</th>
                <th style={{ textAlign: 'right' }}>Seuil alerte</th>
                <th style={{ textAlign: 'right' }} title="Quantité de ce matériau nécessaire pour 1 m²">Coef. /m²</th>
                <th style={{ width: '80px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} data-testid={`material-row-${m.id}`} style={{ background: selectedIds.has(m.id) ? '#EEF2FF' : undefined }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.id)}
                      onChange={() => toggleOne(m.id)}
                      data-testid={`mat-select-${m.id}`}
                      aria-label={`Selectionner ${m.name}`}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PhotoCell item={m} onUpload={(f) => handlePhotoUpload(m.id, f)} onDelete={() => handlePhotoDelete(m.id)} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <button
                          type="button"
                          onClick={() => handleAiGenerate(m.id)}
                          disabled={aiBusyId === m.id}
                          title="Générer avec IA"
                          data-testid={`mat-ai-gen-${m.id}`}
                          style={{ padding: '2px 6px', fontSize: '0.6rem', border: '1px solid #E2E2E6', borderRadius: '4px', background: aiBusyId === m.id ? '#FFF8E1' : '#F4F5F7', cursor: 'pointer', fontWeight: '700', color: '#002FA7' }}
                        >{aiBusyId === m.id ? '…' : 'IA'}</button>
                        <button
                          type="button"
                          onClick={() => handlePhotoFromUrl(m.id)}
                          title="Depuis une URL"
                          data-testid={`mat-url-${m.id}`}
                          style={{ padding: '2px 6px', fontSize: '0.6rem', border: '1px solid #E2E2E6', borderRadius: '4px', background: '#F4F5F7', cursor: 'pointer', fontWeight: '700', color: '#2E7D32' }}
                        >URL</button>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontWeight: '600' }}>{m.name}</td>
                  <td style={{ color: '#5E5F66' }}>{m.category || '-'}</td>
                  <td>{m.unit}</td>
                  <td style={{ textAlign: 'right' }}>{(m.price_ht || 0).toFixed(2)} EUR</td>
                  <td style={{ textAlign: 'right' }}>{m.tva_rate}%</td>
                  <td style={{ textAlign: 'right', fontWeight: '600' }}>{(m.price_ttc || 0).toFixed(2)} EUR</td>
                  <td style={{ textAlign: 'right', color: m.min_threshold > 0 ? '#E65100' : '#9A9AA0' }}>
                    {m.min_threshold > 0 ? `${Math.round(m.min_threshold)} ${m.unit}` : '-'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: '600', color: (m.coefficient_m2 ?? 1) !== 1 ? '#002FA7' : '#9A9AA0' }}>
                    {(m.coefficient_m2 ?? 1) !== 1
                      ? `${Number(m.coefficient_m2).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} ${m.unit}/m²`
                      : '-'}
                  </td>
                  <td style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(m)} data-testid={`edit-${m.id}`}><PencilSimple size={14} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(m)} data-testid={`delete-${m.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="11" style={{ textAlign: 'center', padding: '32px', color: '#5E5F66' }}>Aucun matériau. Ajoutez-en un pour commencer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating bulk-actions bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="mat-bulk-actions"
          style={{
            position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            background: '#0F172A', color: 'white', borderRadius: 12, padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)', zIndex: 800, maxWidth: '92vw',
          }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>
            {selectedIds.size} sélectionné(s)
          </span>
          <button type="button" onClick={() => setBulkCategoryOpen(v => !v)} data-testid="mat-bulk-cat-btn"
            style={{ background: '#1E293B', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            Changer catégorie
          </button>
          {bulkCategoryOpen && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                list="mat-cat-list"
                value={bulkCategoryValue}
                onChange={(e) => setBulkCategoryValue(e.target.value)}
                placeholder="Nouvelle catégorie..."
                data-testid="mat-bulk-cat-input"
                style={{ background: 'white', color: '#0F172A', border: 'none', borderRadius: 6, padding: '5px 8px', fontSize: '0.82rem', width: 180 }}
              />
              <datalist id="mat-cat-list">
                {uniqueCategories.map(c => <option key={c} value={c} />)}
              </datalist>
              <button type="button" onClick={handleBulkCategory} data-testid="mat-bulk-cat-apply"
                style={{ background: '#16A34A', color: 'white', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>OK</button>
            </div>
          )}
          <button type="button" onClick={handleBulkExport} data-testid="mat-bulk-export"
            style={{ background: '#047857', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            Export Excel
          </button>
          <button type="button" onClick={handleBulkDelete} data-testid="mat-bulk-delete"
            style={{ background: '#DC2626', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
            Supprimer
          </button>
          <button type="button" onClick={clearSelection} data-testid="mat-bulk-cancel"
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>
            Annuler
          </button>
        </div>
      )}

      {showModal && (
        <MaterialFormModal
          show={showModal}
          editItem={editId ? items.find(x => x.id === editId) : null}
          onClose={() => { setShowModal(false); setEditId(null); }}
          onSaved={() => { setEditId(null); load(); }}
        />
      )}

      <CategoryManagerModal
        open={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        kind="materiaux"
        onChange={() => load()}
      />
    </div>
  );
};

export default MaterialsCatalogPage;
